/**
 * The one TypeScript list of DataViz types and agent output kinds. DataViz is
 * the one home for outputs, and each item has a type: Dashboard, Web app,
 * Presentation or Video (Adam, 14 and 16 Oct and 3 Nov 2026). "Story board"
 * and "exec report" are words people use, not types.
 *
 * The Go side is goapi/pkg/dataviz. Both are pinned to
 * goapi/pkg/dataviz/dataviz.json by a test, so a drift on either side fails.
 * A new type is one entry here, one in Go, a publisher and a view branch.
 *
 * The stored values do not change: 'dashboard', 'webapp', 'presentation',
 * 'video', 'exec_report' (an alias of dashboard) and 'template' (excluded from
 * the list). Only the label shown here does.
 *
 * A type's family is how it is stored and served. A Presentation is a Web app
 * bundle whose story page is a <pt-deck>: the same view, share view, Download
 * PDF and Publish. The server picks one or the other from the page on every
 * publish. Ask isWebappBundle, never the type by name.
 *
 * Free of DOM-only code: the src/agent vitest project runs in node.
 */
import type { SvgIconComponent } from '@mui/icons-material'
import SpaceDashboardOutlinedIcon from '@mui/icons-material/SpaceDashboardOutlined'
import LanguageIcon from '@mui/icons-material/Language'
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined'
import SlideshowOutlinedIcon from '@mui/icons-material/SlideshowOutlined'

export interface DashboardTypeMeta {
  label: string
  /** Theme palette path for the icon + label, e.g. 'info.main'. */
  color: string
  Icon: SvgIconComponent
}

export interface DatavizType extends DashboardTypeMeta {
  id: string
  /** The agent output kind that publishes as this type, if any. */
  outputKind: string | null
  /** Whether the item opens in the editor or only in the viewer. */
  opens: 'editor' | 'view'
  /** How it is stored and served: a dashboard config, a web app bundle or one video. */
  family: 'dashboard' | 'webapp' | 'video'
}

// #1179: the Type column leads the DataViz list, so each type carries its own
// icon and colour, and the types read apart at a glance.
export const DATAVIZ_TYPES: DatavizType[] = [
  { id: 'dashboard', label: 'Dashboard', color: 'info.main', Icon: SpaceDashboardOutlinedIcon, outputKind: null, opens: 'editor', family: 'dashboard' },
  { id: 'webapp', label: 'Web app', color: 'success.main', Icon: LanguageIcon, outputKind: 'webapp', opens: 'view', family: 'webapp' },
  { id: 'presentation', label: 'Presentation', color: 'warning.main', Icon: SlideshowOutlinedIcon, outputKind: 'webapp', opens: 'view', family: 'webapp' },
  { id: 'video', label: 'Video', color: 'secondary.main', Icon: VideocamOutlinedIcon, outputKind: 'video', opens: 'view', family: 'video' },
]

// exec_report is compiled from an executive-report manifest by
// scripts/workflows/exec-reports/create-dashboard.js. It renders and edits
// exactly like a dashboard, so it shows as one (Adam, 14 Oct 2026).
export const DATAVIZ_ALIASES: Record<string, string> = {
  exec_report: 'dashboard',
}

export interface OutputKindMeta {
  label: string
  extensions: string[]
  datavizType: string | null
}

/** Every kind of output an agent makes, in the order of goapi/pkg/dataviz. */
export const OUTPUT_KINDS: Record<string, OutputKindMeta> = {
  webapp: { label: 'Web app', extensions: [], datavizType: 'webapp' },
  deck: { label: 'Deck', extensions: ['.pptx'], datavizType: null },
  workbook: { label: 'Workbook', extensions: ['.xlsx'], datavizType: null },
  image: { label: 'Image', extensions: ['.png', '.jpg', '.jpeg', '.gif', '.svg', '.webp'], datavizType: null },
  video: { label: 'Video', extensions: ['.mp4', '.mov', '.webm'], datavizType: 'video' },
  document: { label: 'Document', extensions: ['.pdf', '.docx'], datavizType: null },
  table: { label: 'Table', extensions: [], datavizType: null },
  // A dashboard the agent made through create_dashboard (#1886): already a DataViz item.
  dashboard: { label: 'Dashboard', extensions: [], datavizType: null },
}

const FALLBACK_TYPE = DATAVIZ_TYPES[0]

export function dashboardTypeMeta(type: string): DatavizType {
  const id = DATAVIZ_ALIASES[type] ?? type
  return DATAVIZ_TYPES.find(t => t.id === id) ?? FALLBACK_TYPE
}

export function dashboardTypeLabel(type: string): string {
  return dashboardTypeMeta(type).label
}

/** Whether a stored type is served from a web app bundle (config.webapp_config): a Web app or a Presentation. */
export function isWebappBundle(type: string | undefined | null): boolean {
  if (!type) return false
  const id = DATAVIZ_ALIASES[type] ?? type
  return DATAVIZ_TYPES.some(t => t.id === id && t.family === 'webapp')
}

export function outputKindLabel(kind: string): string {
  return OUTPUT_KINDS[kind]?.label ?? kind
}

interface ArtifactShape {
  artifactType?: string
  fileName?: string
  isDir?: boolean
}

/** The output kind of an agent artifact, or null when it is not one of the kinds. */
export function outputKindForArtifact(a: ArtifactShape): string | null {
  if (a.artifactType === 'webapp' && a.isDir !== false) return 'webapp'
  const name = (a.fileName ?? '').toLowerCase()
  const dot = name.lastIndexOf('.')
  if (dot === -1) return null
  const ext = name.slice(dot)
  for (const [kind, meta] of Object.entries(OUTPUT_KINDS)) {
    if (meta.extensions.includes(ext)) return kind
  }
  return null
}

/** The DataViz type this artifact publishes as, or null when it stays in Files. */
export function datavizTypeForArtifact(a: ArtifactShape): string | null {
  const kind = outputKindForArtifact(a)
  return kind ? OUTPUT_KINDS[kind].datavizType : null
}
